import React, { lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { Button } from 'react-bootstrap';
import Skeleton from 'wsm-skeleton';
import { useLabels, usePrefs, useRequestData } from 'ws-scripts/modules/common';
import { addImageParams } from '../utilities/images';
import { setModalSettings } from '../actions/modalSettings';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../utilities/getTargetLocations';
import VehicleImage from './VehicleImage';
import Callout from './Callout';
import './Slick.scss';
import './MediaContainer.scss';

const LazyVehicleImageCarousel = lazy(() => import('./VehicleImageCarousel'));

const MediaContainer = ({
	enableMediaCarousel,
	isTabbedLayout,
	vehicle,
	accountPhone,
	loadingDelayed,
	usePlaceholderImage
}) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const { deviceType, widgetName, windowId } = useRequestData();
	const {
		showVideoModalButton,
		openVideoModalLabel,
		restrictInsertLocationsForVehicles
	} = usePrefs();

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehicleMediaLocation = getVehicleLocationName(
		'vehicle-media',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const { videos = [], uuid, title, link, images = [] } = vehicle;

	const phone = accountPhone;

	let videoButton = null;
	if (showVideoModalButton === 'true' && videos.length > 0) {
		videoButton = (
			<Button
				bsSize={deviceType === 'DESKTOP' ? 'xsmall' : null}
				className={`open-media-modal-btn ${
					deviceType === 'MOBILE' ? 'text-nowrap' : ''
				}`}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Video button',
						result: 'Video modal displayed'
					});

					dispatch(
						setModalSettings({
							isModalOpen: true,
							videos,
							uuid,
							title,
							link,
							phone
						})
					);
				}}
			>
				<i className="ddc-icon ddc-icon-play2 mr-2" />
				<span className="text-link text-link-muted">
					{labels.get(openVideoModalLabel)}
				</span>
			</Button>
		);
	}

	const forceCarouselMatch = document.location.search.match(
		/_forceMediaCarousel=([a-z]*)/
	);

	const showCarousel =
		forceCarouselMatch !== null
			? forceCarouselMatch[1] === 'true'
			: enableMediaCarousel;

	const placeholderImageSrc = usePlaceholderImage
		? addImageParams(images[0].uri)
		: null;

	const mediaSkeleton = (
		<Skeleton skeletonClasses="mb-4" aspectRatio="4by3" />
	);

	// declare vehicleMedia as the skeleton loader
	let vehicleMedia = mediaSkeleton;
	// if loadingDelayed finished, replace skeleton loader with content
	if (!loadingDelayed) {
		// if content is a carousel with more than 1 image
		if (showCarousel && images.length > 1) {
			vehicleMedia = (
				<Suspense fallback={mediaSkeleton}>
					<Skeleton
						skeletonClasses="mb-4 vehicle-image-skeleton"
						aspectRatio="4by3"
						placeholderImage={placeholderImageSrc}
					/>
					<LazyVehicleImageCarousel
						images={images}
						link={vehicle.link}
						uuid={vehicle.uuid}
						videoButton={videoButton}
					/>
				</Suspense>
			);
			// if content is a single image
		} else {
			vehicleMedia = (
				<VehicleImage
					image={images[0]}
					imageIndex={0}
					enableImage
					link={vehicle.link}
					uuid={vehicle.uuid}
				/>
			);
		}
	}
	return (
		<div className="vehicle-card-media-container">
			{vehicleMedia}
			{videoButton}
			{isTabbedLayout && (
				<ul
					className="list-unstyled d-flex flex-column vehicle-card-media align-items-center hidden"
					data-location={vehicleMediaLocation}
				/>
			)}
			{!isTabbedLayout && (
				<Callout
					callouts={vehicle.callout}
					vehicle={vehicle}
					{...{
						isTabbedLayout
					}}
				/>
			)}
		</div>
	);
};

MediaContainer.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		images: PropTypes.arrayOf(PropTypes.shape({})),
		uuid: PropTypes.string,
		link: PropTypes.string,
		title: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		phone: PropTypes.string,
		callout: PropTypes.arrayOf(PropTypes.shape({})),
		videos: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	isTabbedLayout: PropTypes.bool.isRequired,
	accountPhone: PropTypes.string.isRequired,
	loadingDelayed: PropTypes.bool,
	usePlaceholderImage: PropTypes.bool
};

MediaContainer.defaultProps = {
	loadingDelayed: false
};

export default MediaContainer;
